/*
 * The Case IDE's URL is a mirror of what is on screen: `?open=kind:id` for the
 * active artifact and `?thread=id` for the chat thread. Written with
 * `replaceState` (no history entry, no router re-render) so every tab click
 * and thread switch leaves a URL that reloads, or shares, to the same view.
 */
import type { Artifact } from "./types";
import { OPEN_SEARCH_PARAM } from "./routes";

export const THREAD_PARAM = "thread";

export const openValue = (
  artifact: Pick<Artifact, "kind" | "id"> | null | undefined
): string | null => (artifact ? `${artifact.kind}:${artifact.id}` : null);

/** Set (or, for null, drop) search params on the current URL in place. */
export const writeSearchParams = (patch: Record<string, string | null>) => {
  if (typeof window === "undefined") return;
  const url = new URL(window.location.href);
  let changed = false;
  for (const [key, value] of Object.entries(patch)) {
    const current = url.searchParams.get(key);
    if (current !== value) {
      if (value === null) url.searchParams.delete(key);
      else url.searchParams.set(key, value);
      changed = true;
    }
  }
  if (!changed) return;
  // Keep the router's own history state (its key and index) on the entry.
  window.history.replaceState(
    window.history.state,
    "",
    `${url.pathname}${url.search}${url.hash}`
  );
};

/** Drop the router's `state` from the entry in place, so a reload doesn't replay it. */
export const dropRouterState = () => {
  if (typeof window === "undefined") return;
  const current: unknown = window.history.state;
  if (!current || typeof current !== "object") return;
  window.history.replaceState({ ...current, usr: null }, "");
};

/**
 * A link that opens this artifact in a fresh tab, with the chat thread the
 * URL already carries.
 */
export const shareableLink = (
  artifact: Pick<Artifact, "kind" | "id">,
  href: string = typeof window === "undefined" ? "" : window.location.href
): string => {
  const url = new URL(href);
  url.searchParams.set(OPEN_SEARCH_PARAM, openValue(artifact) ?? "");
  return url.toString();
};
